/**
 * @jsxRuntime classic
 * @jsx jsx
 */

/**
 * Custom nudge content for the FOMO ("teammates using Rovo") nudge.
 *
 * Rendered via the `customContent` slot on the ConversationAction — this
 * replaces the standard icon+title layout inside the nudge pill while keeping
 * the outer pill container, dismiss button, animation, and analytics intact.
 *
 * Layout:
 *   title [👤👤👤]
 *   description (always visible)
 *
 * Both title and description are always visible (no hover required).
 */

import React from 'react';

import { jsx, cssMap } from '@compiled/react';

import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { FomoCollaborator } from '../types';

import { FomoNudgeIcon } from './FomoNudgeIcon';

const styles = cssMap({
	wrapper: {
		textAlign: 'left',
		paddingTop: token('space.050'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.150'),
		cursor: 'pointer',
	},
});

export interface FomoNudgeContentProps {
	collaborators: ReadonlyArray<FomoCollaborator>;
	description: string;
	title: string;
}

export function FomoNudgeContent({
	collaborators,
	title,
	description,
}: FomoNudgeContentProps): React.JSX.Element {
	return (
		<div css={styles.wrapper}>
			<Stack space="space.050">
				<Inline space="space.100" alignBlock="center">
					<Text color="color.text.inverse">{title}</Text>
					<FomoNudgeIcon collaborators={collaborators} />
				</Inline>
				<Text size="small" color="color.text.inverse">
					{description}
				</Text>
			</Stack>
		</div>
	);
}
